import React from "react";
import {
	Box,
	Chip,
	LinearProgress,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { Launch } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/system";
import { useNavigate } from "react-router-dom";
import DatagridComponent from "../../../../components/datagridComponent";
import useStyles from "../styles";
import ClienteComTipo from "../components/clienteComTipo";

/**
 * Coluna esquerda da aba ONU: tabela de ONUs.
 * A barra de abas CPE/ONU e o painel direito (Atendimentos/Potência) vivem no
 * index.js. A linha selecionada é elevada via `setSelectedRow`.
 */
export default function TabOnu({
	filteredRows,
	loading,
	setSelectedRow,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);
	const navigate = useNavigate();

	const handleLinkClick = (e, params) => {
		e.stopPropagation();

		const clientStorage = {
			interfaceId: params?.row?.onuItem,
			clienteId: params?.row?.clienteId,
			hostId: params?.row?.hostId,
			device: params?.row?.device,
			selectTime: 5,
			initialDate: null,
			finalDate: null,
			serviceId: params?.row?.servicoId,
		};

		localStorage.setItem("filterData", JSON.stringify(clientStorage));

		if (e.button === 1 || e.ctrlKey || e.metaKey) {
			window.open(`/interno/dash/onu`, "_blank");
		} else {
			navigate(`/interno/dash/onu`);
		}
	};

	const linkView = (params) => {
		return (
			<Box sx={styles.launchContainer}>
				<Tooltip title={"Navegar até a ONU"}>
					<Launch
						size="small"
						sx={{ ...styles.launchIcon, cursor: "pointer" }}
						onClick={(e) => handleLinkClick(e, params)}
						onAuxClick={(e) => {
							if (e.button === 1) {
								handleLinkClick(e, params);
							}
						}}
					/>
				</Tooltip>
			</Box>
		);
	};

	const columnsOnu = [
		{ field: "id", headerName: "ID", hide: true },
		{
			field: "nome",
			headerName: "Cliente",
			flex: 1,
			renderCell: (params) => (
				<ClienteComTipo
					nome={params?.row?.nome || ""}
					tipoCliente={params?.row?.tipoCliente}
				/>
			),
		},
		{
			field: "onuItem",
			headerName: "ONU",
			flex: 1,
			renderCell: (params) => (
				<Tooltip title={params?.value || ""}>
					<Typography
						sx={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							width: "100%",
							fontSize: "inherit",
						}}
					>
						{params?.value}
					</Typography>
				</Tooltip>
			),
		},
		{
			field: "host",
			headerName: "Host",
			flex: 1.5,
		},
		{
			field: "potencia",
			headerName: "Potência",
			flex: 0.6,
			valueGetter: (value, row) => {
				const power = row?.receivedPower;
				return power !== null && power !== undefined
					? `${Number(power).toFixed(2)} dBm`
					: "N/A";
			},
		},
		{
			field: "receivedPower",
			headerName: "Status",
			flex: 0.6,
			renderCell: (params) => {
				const raw = params?.row?.receivedPower;
				const value = parseFloat(raw);
				const isNA = raw === null || raw === undefined || isNaN(value);

				const getChipColor = (val) => {
					if (isNA) return "neutral";
					if (val <= -27) return "critical";
					if (val <= -25) return "high";
					return "success";
				};
				const getChipLabel = (val) => {
					if (isNA) return "N/A";
					if (val <= -27) return "Crítica";
					if (val <= -25) return "Atenção";
					return "Ok";
				};

				const colorKey = getChipColor(value);
				const isNeutral = colorKey === "neutral";

				return (
					<Chip
						label={getChipLabel(value)}
						sx={{
							backgroundColor: isNeutral
								? "transparent"
								: theme.palette.chip.background[colorKey],
							color: isNeutral
								? theme.palette.text.disabled
								: theme.palette.chip.colorText[colorKey],
							border: "0.6px solid",
							borderColor: isNeutral
								? theme.palette.text.disabled
								: theme.palette.chip.colorText[colorKey],
							borderRadius: "10px",
							height: "20px",
							width: "70px",
						}}
						size="small"
					/>
				);
			},
		},
		{
			field: "link",
			headerName: "",
			sortable: false,
			width: 0.1,
			renderCell: linkView,
		},
	];

	return (
		<Paper
			sx={{
				...styles.paperClientes,
				borderTopLeftRadius: 0,
				position: "relative",
			}}
			//	elevation={theme.palette.mode === "dark" ? 0 : 1}
			variant="outlined"
		>
			{loading && <LinearProgress sx={styles.linearProgress} />}
			<DatagridComponent
				columns={columnsOnu}
				rows={filteredRows || []}
				setSelectedRow={setSelectedRow}
				isPagination={false}
				onRowClick={(params) => setSelectedRow(params.row)}
				sxDatagrid={{
					height: "100%",
					borderRadius: "10px",
					"& .MuiDataGrid-row.Mui-selected": {
						backgroundImage: theme.palette.gradient.buttonSelected,
						"& .MuiDataGrid-cell": { color: theme.palette.white.main },
					},
					"& .MuiDataGrid-row:hover": {
						backgroundImage: `linear-gradient(
                                                        90deg,
                                                        ${alpha(theme.palette.buttonHoverGradient.dark, 0.5)} 0%,
                                                        ${alpha(theme.palette.buttonHoverGradient.light, 0.5)} 100%
                                                    )`,
					},
				}}
				options={{ disableRowSelectionOnClick: false }}
			/>
		</Paper>
	);
}
